/**
 * Wave 317 · Littlest Gênese · the viewer.
 *
 * Premise (locked): encantar o comprador da casa. Não é diagrama de
 * engenharia. Não é "tecnicamente correto." É "fall in love with the
 * place" quando alguém abre o site na cama.
 *
 * Architecture:
 *   ProjectSpecs (D1) → spec-bindings → Program + Dimensions
 *                                    ↓
 *                                 composer → ComposedScene
 *                                    ↓
 *                          BLOCK_REGISTRY[blockId] → R3F primitives
 *
 * Day 1 reality: blocks are PROCEDURAL placeholders (intentional toy-like
 * stylization, MeshToon, warm palette). Days 2-3 swap each procedural
 * block for an authored .glb via `useGLTF` — same component name, same
 * props, swap the body. The composer doesn't know or care.
 *
 * Camera: OrbitControls + auto-rotate-when-idle. NO scroll-locked
 * cinematics. The visitor explores; the scene lives.
 *
 * Lighting: ambient + warm directional + subtle hemisphere. No HDRI, no
 * tone mapping (stylized, not photoreal). Soft shadows on receivers,
 * cast on the few main blocks.
 */

import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { ProjectSpecs } from '../lib/api';
import { composeScene } from '../lib/littlest/composer';
import { BLOCK_REGISTRY } from '../lib/littlest/blocks';
import { deriveProgram, deriveDimensions } from '../lib/littlest/spec-bindings';
import { PALETTE } from '../lib/littlest/types';

interface LittlestGeneseProps {
  specs: ProjectSpecs;
  /**
   * If true, dim ambient motion (CSS `prefers-reduced-motion` is also
   * respected inside the blocks, but this lets the parent override
   * — useful when the scene is in a tab that's not visible, etc.).
   */
  reduceMotion?: boolean;
}

export function LittlestGenese({ specs }: LittlestGeneseProps): JSX.Element {
  const program = deriveProgram(specs);
  const dimensions = deriveDimensions(specs);
  const composed = composeScene(program, dimensions);

  return (
    <section
      data-littlest-section
      className="relative h-[100vh] w-full overflow-hidden bg-[color:var(--lt-bg,#1a1612)]"
    >
      <Canvas
        shadows
        camera={{
          position: composed.camera.position,
          fov: 38,
        }}
        gl={{
          antialias: true,
          toneMapping: THREE.NoToneMapping, // stylized, not photoreal
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        dpr={[1, 2]}
      >
        {/* Wave 317 D1 fix · canvas background fallback. The Sky block's
            shader paints the visible sphere; this color is what the user
            sees if the camera somehow ends up beyond the sphere or while
            the shader compiles. Warm sunset peach — same family as the
            sky shader's bottom stop. */}
        <color attach="background" args={[PALETTE.sky]} />

        {/* ── Lighting ──────────────────────────────────────────── */}
        {/* Hemisphere = sky/ground bounce; the base "color of the air." */}
        <hemisphereLight args={[PALETTE.sky, PALETTE.ground, 0.7]} />
        {/* Ambient = global fill so toon shading doesn't black-crush. */}
        <ambientLight intensity={0.6} />
        {/* Directional = the "sun." Warm, low angle = sunset feel.
            Soft shadows on receivers (ground, deck). */}
        <directionalLight
          position={[10, 14, 8]}
          intensity={1.05}
          color={PALETTE.sky}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-far={50}
          shadow-camera-left={-25}
          shadow-camera-right={25}
          shadow-camera-top={20}
          shadow-camera-bottom={-20}
        />
        {/* Cool fill from opposite side so shadowed faces don't go pure
            black — important for stylized scenes where the eye reads
            shadow as "dead area" otherwise. */}
        <directionalLight position={[-8, 6, -8]} intensity={0.3} color={PALETTE.skyTop} />

        <Suspense fallback={null}>
          {composed.instances.map((inst) => {
            const Block = BLOCK_REGISTRY[inst.blockId];
            return <Block key={inst.key} instance={inst} />;
          })}
        </Suspense>

        {/* ── Camera controls ───────────────────────────────────── */}
        {/* OrbitControls + auto-rotate is the Littlest Tokyo default.
            Visitor stops the rotation by interacting; auto-resumes
            after a few seconds of idle (default behavior). */}
        <OrbitControls
          enablePan={false}
          // Wave 317 D1.5 · BUG FIX (JP feedback): wheel-zoom estava
          // capturando o scroll da página quando o mouse passava sobre
          // o canvas. Visitor ficava "preso" no 3D. Tirando o zoom-on-
          // wheel, o scroll passa direto pra página. Rotacionar continua
          // funcionando (left-drag), e em mobile o pinch ainda zoom-a.
          enableZoom={false}
          autoRotate
          autoRotateSpeed={0.45}
          target={composed.camera.target}
          minPolarAngle={Math.PI / 6}     // don't let user go fully above
          maxPolarAngle={Math.PI / 2.05}  // don't let user clip into ground
        />
      </Canvas>

      {/* Wave 317 D1.5 · "continuar pra baixo" affordance.
          A cena é uma chapter dentro de uma scrolly. Sem isso, o usuário
          que tá explorando o 3D não tem hint visual de que tem mais
          conteúdo abaixo. Botão minimalista, ↓ animado, scroll suave
          pro próximo bloco. */}
      <button
        type="button"
        onClick={() => {
          // Find the next sibling section after the canvas's parent and
          // scroll to it. Falls back to scrolling by viewport height.
          const me = document.querySelector('[data-littlest-section]');
          const next = me?.nextElementSibling as HTMLElement | null;
          if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
        }}
        aria-label="Continuar para a próxima seção"
        className="group absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex flex-col items-center gap-1 rounded-full border border-white/20 bg-black/30 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-white/70 backdrop-blur-md transition-all duration-200 ease-out hover:border-white/40 hover:bg-black/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <span>continuar</span>
        <svg
          aria-hidden
          width="14"
          height="14"
          viewBox="0 0 14 14"
          className="transition-transform duration-300 ease-out group-hover:translate-y-[2px] motion-safe:animate-[bounce_2s_ease-in-out_infinite]"
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Day 1 watermark — strip in Day 4 once look is locked. */}
      <div className="pointer-events-none absolute bottom-3 right-4 text-[10px] uppercase tracking-[0.16em] text-white/30">
        Wave 317 · Littlest Gênese · v0
      </div>
    </section>
  );
}
